import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import api from "@/lib/api";
import { F } from "@/components/admin/RatesAdmin";

const empty = { name: "", code: "", service: "", remarks: "", logo_url: "", active: true };

export default function LinesAdmin() {
  const [lines, setLines] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/shipping-lines").then(({ data }) => setLines(data));
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name) { toast.error("Name is required"); return; }
    try {
      if (editId) await api.put(`/shipping-lines/${editId}`, form);
      else await api.post("/shipping-lines", form);
      toast.success("Saved"); setOpen(false); load();
    } catch { toast.error("Save failed"); }
  };
  const del = async (id) => { await api.delete(`/shipping-lines/${id}`); toast.success("Deleted"); load(); };

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 400 * 1024) { toast.error("Logo must be under 400KB"); return; }
    const reader = new FileReader();
    reader.onload = () => set("logo_url", reader.result);
    reader.readAsDataURL(file);
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button size="sm" className="bg-signal text-white rounded-sm" onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} data-testid="add-line-btn"><Plus className="h-4 w-4 mr-1.5" />Add Shipping Line</Button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {lines.map((l) => (
          <div key={l.id} className="border border-border rounded-md p-5 bg-card" data-testid={`admin-line-${l.id}`}>
            <div className="flex justify-between items-start">
              <div className="flex items-center gap-3">
                {l.logo_url ? <img src={l.logo_url} alt={l.name} className="h-10 w-10 object-contain rounded-sm border border-border bg-white" /> : <div className="h-10 w-10 rounded-sm bg-navy flex items-center justify-center text-cyan text-xs font-bold">{(l.name || "?").slice(0, 2).toUpperCase()}</div>}
                <div>
                  <div className="font-heading font-bold text-navy">{l.name}</div>
                  <div className="text-xs font-mono-data text-muted-foreground mt-1">{l.code} · {l.service}</div>
                </div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => { setForm(l); setEditId(l.id); setOpen(true); }} className="p-1.5 hover:bg-secondary rounded-sm" data-testid={`edit-line-${l.id}`}><Pencil className="h-3.5 w-3.5 text-navy" /></button>
                <button onClick={() => del(l.id)} className="p-1.5 hover:bg-secondary rounded-sm" data-testid={`del-line-${l.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">{l.remarks}</p>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Edit" : "Add"} Shipping Line</DialogTitle><DialogDescription className="sr-only">Shipping line details form</DialogDescription></DialogHeader>
          <div className="grid gap-4">
            <F label="Name"><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-sm" data-testid="lf-name" /></F>
            <F label="SCAC / Code"><Input value={form.code} onChange={(e) => set("code", e.target.value)} className="rounded-sm" data-testid="lf-code" /></F>
            <F label="Service / Route"><Input value={form.service} onChange={(e) => set("service", e.target.value)} className="rounded-sm" data-testid="lf-service" /></F>
            <F label="Remarks"><Input value={form.remarks} onChange={(e) => set("remarks", e.target.value)} className="rounded-sm" data-testid="lf-remarks" /></F>
            <F label="Logo">
              <div className="flex items-center gap-3">
                {form.logo_url ? <img src={form.logo_url} alt="logo" className="h-12 w-12 object-contain rounded-sm border border-border bg-white" /> : <div className="h-12 w-12 rounded-sm bg-secondary flex items-center justify-center text-xs text-muted-foreground">No logo</div>}
                <label className="inline-flex items-center h-9 px-3 text-sm border border-border rounded-sm cursor-pointer hover:bg-secondary">
                  <input type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="lf-logo-input" />Upload Logo
                </label>
                {form.logo_url && <button type="button" onClick={() => set("logo_url", "")} className="text-xs text-destructive underline" data-testid="lf-logo-clear">Remove</button>}
              </div>
            </F>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-signal text-white rounded-sm" onClick={save} data-testid="lf-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
